import { useState } from 'react';
import {
	DateInput,
	EditBase,
	Form,
	required,
	SaveButton,
	SelectInput,
	Toolbar,
	useDataProvider,
	useGetIdentity,
	useRecordContext,
	useTranslate,
	useUpdate
} from 'react-admin';
import ChangeCircle from '@mui/icons-material/ChangeCircle';
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Tooltip
} from '@mui/material';
import { DialogCloseButton } from '../../../misc/DialogCloseButton';
import { INPUT_SOURCE } from '../../constants';
import { useObjectStatuses } from '../../hooks';

export const ChangeObjectStatus = ({
	resource = 'properties'
}: {
	resource?: string;
}) => {
	const t = useTranslate();
	const { identity } = useGetIdentity();
	const dataProvider = useDataProvider();
	const [update] = useUpdate();
	const { objectStatuses } = useObjectStatuses();
	const record = useRecordContext();
	const [open, setOpen] = useState(false);
	const handleOpen = () => {
		setOpen(true);
	};

	if (!record) return null;

	const handleSuccess = async ({ status_data: statusData }: any) => {
		setOpen(false);
		const object = await dataProvider.getOne(resource, {
			id: record.id
		});
		if (!object.data) return;

		const { status_id, date } = statusData;

		await update(resource, {
			id: object.data.id,
			data: {
				...object.data,
				status_data: {
					date,
					status_id
				}
			},
			previousData: object.data
		});
	};

	if (!identity) return null;

	return (
		<>
			<Tooltip title={t('objects.page.changeStatus')}>
				<IconButton
					size="small"
					sx={{
						color: 'text.secondary'
					}}
					onClick={handleOpen}
				>
					<ChangeCircle fontSize="inherit" color="primary" />
				</IconButton>
			</Tooltip>
			<EditBase
				resource={resource}
				transform={data => {
					const statusDate = new Date(data.set_status_date);
					statusDate.setHours(0, 0, 0, 0);
					data.set_status_date = statusDate.toISOString();

					return {
						status_data: {
							date: new Date(data.set_status_date).toISOString(),
							status_id: data.status
						}
					};
				}}
				mutationOptions={{ onSuccess: handleSuccess }}
			>
				<Dialog
					open={open}
					onClose={() => setOpen(false)}
					aria-labelledby="form-dialog-title"
					fullWidth
					disableRestoreFocus
					maxWidth="sm"
				>
					<Form>
						<DialogCloseButton onClose={() => setOpen(false)} />
						<DialogTitle id="form-dialog-title">
							{t('objects.page.changeStatus')}
						</DialogTitle>
						<DialogContent>
							<Stack gap={2}>
								<Stack direction="row" spacing={1}>
									<SelectInput
										source={INPUT_SOURCE.STATUS}
										label="objects.page.status"
										validate={required()}
										defaultValue={record.status_data.status_id || 1}
										optionText={({ name, color }) => (
											<div>
												{name}{' '}
												<Tooltip title={name} placement="top">
													<Box
														marginLeft={0.5}
														width={10}
														height={10}
														display="inline-block"
														borderRadius="5px"
														bgcolor={color}
														component="span"
													/>
												</Tooltip>
											</div>
										)}
										choices={objectStatuses}
										helperText={false}
									/>
									<DateInput
										source={INPUT_SOURCE.SET_STATUS_DATE}
										defaultValue={new Date().toISOString().slice(0, 10)}
										label="objects.page.setStatusDate"
										validate={required()}
										helperText={false}
									/>
								</Stack>
							</Stack>
						</DialogContent>
						<DialogActions sx={{ p: 0 }}>
							<Toolbar sx={{ width: '100%' }}>
								<SaveButton />
							</Toolbar>
						</DialogActions>
					</Form>
				</Dialog>
			</EditBase>
		</>
	);
};
